// The count on the installed app icon.
//
// Uses the Badging API, which draws on the icon of a PWA someone has added to
// their home screen or dock. Not every browser has it and nothing here is
// required for the app to work, so every call is feature-detected and every
// failure is swallowed — a badge is a nicety, and it must never be the reason
// something breaks.
//
// Worth knowing about the limits, because they shape what this can promise:
//
//  - There is no service worker, so nothing updates the badge while the app is
//    closed. The number is written whenever the app reads its notifications,
//    and then sits on the icon until the next time it is opened. It is "what
//    was waiting when you last looked", not a live count.
//  - iOS and iPadOS only draw the badge for an installed web app whose
//    notification permission has been granted. Everywhere else it just works.
//    `badgePermissionNeeded` is how the UI knows to offer that, rather than
//    this file springing a permission prompt on somebody unasked.

const supported = () =>
  typeof navigator !== "undefined" && typeof navigator.setAppBadge === "function";

/** True when the API exists but the platform will not draw it without asking. */
export function badgePermissionNeeded() {
  if (!supported()) return false;
  if (typeof Notification === "undefined") return false;
  return Notification.permission === "default";
}

/** Asks for the permission the badge needs. Only ever called from a click. */
export async function requestBadgePermission() {
  if (typeof Notification === "undefined") return false;
  try {
    return (await Notification.requestPermission()) === "granted";
  } catch {
    return false;
  }
}

/** Writes the count, or clears the badge when there is nothing waiting. */
export function setBadge(count) {
  if (!supported()) return;
  try {
    const n = Number(count) || 0;
    // A promise rejection here is normal — an uninstalled site, or permission
    // not granted — so it is caught rather than surfaced.
    if (n > 0) Promise.resolve(navigator.setAppBadge(n)).catch(() => {});
    else if (typeof navigator.clearAppBadge === "function") {
      Promise.resolve(navigator.clearAppBadge()).catch(() => {});
    }
  } catch {
    /* nothing here is worth interrupting the app for */
  }
}
